import { useSignal } from "@preact/signals";
import { useRef } from "preact/hooks";
import IconArrowUp from "tabler-icons/arrow-up.tsx";
import IconX from "tabler-icons/x.tsx";
import IconUser from "tabler-icons/user.tsx";
import { AIMessage } from "../../lib/ai/types.d.ts";

export function GageAI() {
  const chatOpen = useSignal(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const generating = useSignal(false);
  const inputText = useSignal('');
  const messages = useSignal<AIMessage[]>([]);
  const messagesRef = useRef<HTMLDivElement>(null);

  function close(e: Event) {
    e.preventDefault();
    dialogRef.current?.close();
    chatOpen.value = false;
  }

  function open() {
    chatOpen.value = true;
    scrollToBottom();
  }

  function submit(e: Event) {
    scrollToBottom(500);

    e.preventDefault();

    messages.value = [
      ...messages.value,
      {
        role: "user",
        content: inputText.value,
      },
    ]

    inputText.value = '';

    generate();
  }

  async function generate() {
    const message: AIMessage = {
      role: "assistant",
      content: ''
    }

    try {
      messages.value.push(message);
      generating.value = true;

      const res = await fetch('/api/ai', {
        method: "POST",
        body: JSON.stringify(messages.value)
      })

      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const reader = res.body!.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });

        if (chunk.startsWith('data: ')) {
          const lines = chunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6);
              if (data === '[DONE]') break;

              try {
                const newMessage = JSON.parse(data);
                message.content += newMessage.content;
                message.html = newMessage.html;
              } catch (e) {
                console.error(e);
              }
            }
          }
        } else {
          // If it's plain text streaming
          message.content += chunk;
        }

        messages.value = [...messages.value];
        scrollToBottom(100);
      }

    } catch (e) {
      console.error(e);
      message.html = '<p style="color:red;">Error generating response</p>';
    }
    finally {
      generating.value = false;
      messages.value = [...messages.value];
    }
  }

  async function scrollToBottom(dist = 0) {
    if (!messagesRef.current || dist && messagesRef.current.scrollTop <= messagesRef.current.scrollHeight - messagesRef.current.clientHeight - dist) return;
    await new Promise(r => setTimeout(r, 50));
    if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(e);
    }
  }

  return <>

    <dialog class="gage-ai-chat" ref={dialogRef} onClose={close} onBeforeToggle={open}>

      <button onClick={close} class="close"><IconX height={24} /></button>

      <div class="chat">
        {!!messages.value.length && <div class="messages" ref={messagesRef}>
          {messages.value.map((m, i) => <div class="message" key={i} data-role={m.role}>
            <div className="avatar">
              {m.role == "assistant" ? <img src="/img/logos/1stage1glogoa.svg" alt="Gage Technologies AI" /> : <IconUser />}
            </div>
            <div className="content" dangerouslySetInnerHTML={m.html ? { __html: m.html } : undefined}>
              {m.content}
            </div>
          </div>)}
        </div>}

        {!messages.value.length && <div class="title">
          <h2>Ask <img src="/img/logos/gage-themed.svg" height={30} alt="Gage" /> AI</h2>
        </div>}

        <form class="input" onClick={e => e.currentTarget.querySelector('textarea')?.focus()} onSubmit={submit}>
          <textarea rows={1} onInput={(e) => inputText.value = e.currentTarget.value} value={inputText.value} onKeyDown={onKeyDown}
            placeholder="Ask about Gage..." autofocus autocomplete="off"
          ></textarea>
          <div className="toolbar">
            <div className="right">
              <button type="submit" disabled={!inputText.value || generating.value}><IconArrowUp /></button>
            </div>
          </div>
        </form>
      </div>
    </dialog>

  </>
}

